import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  KeyRound,
  PlugZap,
  RefreshCw,
  Sparkles,
  Store,
} from "lucide-react";
import { cn } from "../../../utils/cn";

// ─────────────────────────────────────────────────────────────────────────────
// Anuncio: actualización del plugin WooCommerce (v2, julio 2026).
//
// Muchas tiendas corren versiones < 1.3.0 del plugin, anteriores al
// auto-updater, y deben actualizar a mano una última vez; desde ahí el plugin
// se actualiza solo. Elegible únicamente para comercios con integración Woo.
//
// Contrato de definición (ver registry.js): hook que recibe el contexto
// compartido del AnnouncementsHost y devuelve null (no elegible) o el
// descriptor { id, title, steps, cta } que consume AnnouncementModal.
// ─────────────────────────────────────────────────────────────────────────────

// Misma fuente que AddStoreConnectModal: update.json trae SIEMPRE la
// download_url vigente del plugin publicado en gh-pages.
const WOO_UPDATE_URL =
  "https://beartrackapp.github.io/woocommerce-connector/update.json";

export default function useWooPluginUpdateV2(ctx) {
  const { t } = useTranslation();
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);

  const eligible =
    ctx.isClient &&
    (ctx.integrations || []).some((i) => i.integrationName === "WooCommerce");

  // download_url vigente, solo si el anuncio aplica.
  useEffect(() => {
    if (!eligible) return undefined;
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetch(WOO_UPDATE_URL, { signal: ctrl.signal });
        if (!res.ok) return;
        const json = await res.json();
        if (json?.download_url) setDownloadUrl(json.download_url);
        if (json?.version) setLatestVersion(json.version);
      } catch {
        // Sin enlace directo el paso 2 igual explica dónde descargar.
      }
    })();
    return () => ctrl.abort();
  }, [eligible]);

  return useMemo(() => {
    if (!eligible) return null;

    return {
      id: "woo-plugin-v2-2026-07",
      title: t("Novedades de WooCommerce"),
      cta: {
        label: t("Ir a Integraciones"),
        icon: <Store className="h-4 w-4" aria-hidden="true" />,
        to: "/home/integrations",
      },
      steps: [
        {
          icon: <Sparkles className="h-7 w-7" aria-hidden="true" />,
          title: t("Mejoramos tu integración WooCommerce"),
          body: (
            <>
              <p>
                {t(
                  "Nueva versión del plugin con clave de conexión propia por tienda, reglas para elegir qué pedidos se envían a BearTrack (comuna, método de envío, monto y más) y actualizaciones automáticas de ahí en adelante.",
                )}
              </p>
              <p className="mt-2 font-medium text-fg">
                {t(
                  "Si tu plugin es anterior a la versión 1.3.0, necesita una última actualización manual. Te mostramos cómo en 4 pasos.",
                )}
              </p>
            </>
          ),
        },
        {
          icon: <Download className="h-7 w-7" aria-hidden="true" />,
          title: t("1. Descarga la última versión"),
          body: (
            <>
              <p>
                {t(
                  "Baja el ZIP del plugin actualizado. También lo encuentras en Integraciones → WooCommerce → Agregar cuenta.",
                )}
              </p>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={cn(
                    "mt-3 inline-flex h-9 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium",
                    "bg-brand-500 text-white transition-colors hover:bg-brand-600 active:bg-brand-700",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
                  )}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  {latestVersion
                    ? t("Descargar plugin v{{version}}", {
                        version: latestVersion,
                      })
                    : t("Descargar plugin")}
                </a>
              )}
            </>
          ),
        },
        {
          icon: <RefreshCw className="h-7 w-7" aria-hidden="true" />,
          title: t("2. Actualízalo en WordPress"),
          body: (
            <p>
              {t(
                "En tu WordPress ve a Plugins → Añadir nuevo → Subir plugin, elige el ZIP y confirma con \"Reemplazar el actual con el subido\". Tus ajustes y pedidos no se pierden.",
              )}
            </p>
          ),
        },
        {
          icon: <KeyRound className="h-7 w-7" aria-hidden="true" />,
          title: t("3. Genera tu clave de conexión"),
          body: (
            <p>
              {t(
                "En este portal, entra a Integraciones y usa el botón de llave junto a tu tienda WooCommerce para generar la clave. Se muestra una sola vez: cópiala.",
              )}
            </p>
          ),
        },
        {
          icon: <PlugZap className="h-7 w-7" aria-hidden="true" />,
          title: t("4. Pega la clave en tu tienda"),
          body: (
            <>
              <p>
                {t(
                  "En WordPress ve a WooCommerce → Beartrack, pega la clave y presiona \"Guardar y probar conexión\". ¡Listo! Desde ahora el plugin se actualiza solo.",
                )}
              </p>
              <p className="mt-2 text-fg-muted">
                {t(
                  "Mientras no configures la clave, el plugin sigue funcionando en modo compatibilidad — pero las reglas de pedidos requieren este paso.",
                )}
              </p>
            </>
          ),
        },
      ],
    };
  }, [eligible, t, downloadUrl, latestVersion]);
}
